import {assocPath, clone, isNil} from 'ramda'

type Path = string

/**
 * Splits a dot/bracket-notation path string into an array of keys.
 * Numeric segments are coerced to integers so that `assocPath` creates
 * arrays for indexed positions (matching lodash/fp/set behaviour).
 *
 * Limitation: quoted bracket notation like `'a["b.c"]'` is NOT supported —
 * the inner dot would be treated as a separator.
 */
const parsePath = (path: string): (string | number)[] =>
  path
    .split(/[.[\]]+/)
    .filter(Boolean)
    .map(key => (/^\d+$/.test(key) ? Number(key) : key))

/**
 * Function for setting values in nested objects.
 * It is assumed that the type of the object is not modified but this is *not* guaranteed
 *
 * @param updatesObject - object with keys that are paths to values
 * @param object - object to update
 * @returns updated object
 */
export const setNestedValues = (
  updatesObject: Record<Path, unknown>,
  object: Record<string, unknown>
) => {
  let updatedObject = clone(object)

  for (const [path, value] of Object.entries(updatesObject)) {
    updatedObject = assocPath(parsePath(path), value, updatedObject) as Record<
      string,
      unknown
    >
  }

  return updatedObject
}

/**
 * Convert a value to an array.
 * If the value is null or undefined, return an empty array.
 * If the value is an array, return the array.
 * If the value is not an array, return an array with the value.
 *
 * @param value - The value to convert to an array.
 * @returns The value as an array.
 */
export function toArray<T>(value: T | T[] | null | undefined): T[] {
  if (isNil(value)) return []
  return Array.isArray(value) ? value : [value]
}
